import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import { SharedButton, SharedCard, SharedFormField, SharedProgressBar } from "../../components/shared";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import { useSellerState, setJourneyMode } from "../../js/state/sellerState";
import { sellerProgress } from "../../js/utils/sellerSteps";
import { useAutosave } from "../../js/hooks/useAutosave";

export default function SellerWelcome() {
  const navigate = useNavigate();
  const { seller, updateSeller, updatePortfolio } = useSellerState();
  const saved = seller.portfolio.welcome || {};
  const progress = sellerProgress(seller.portfolio);
  const [form, setForm] = useState({
    ownerName: saved.ownerName || seller.name || "",
    email: saved.email || seller.email || "",
    phone: saved.phone || seller.phone || "",
    occupancy: saved.occupancy || "",
    timeline: saved.timeline || "",
    reason: saved.reason || "",
    notes: saved.notes || "",
  });

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  const persistWelcome = useCallback(
    (next) => {
      updateSeller({
        name: next.ownerName,
        email: next.email,
        phone: next.phone,
      });
      updatePortfolio("welcome", next);
    },
    [updatePortfolio, updateSeller]
  );

  useAutosave(form, persistWelcome);

  function beginPortfolio() {
    setJourneyMode("seller");
    updateSeller({
      name: form.ownerName,
      email: form.email,
      phone: form.phone,
    });
    updatePortfolio("welcome", form);
    navigate("/seller/property-details");
  }

  return (
    <AppLayout variant="seller" showSellerNav>
      <section className="hero-panel" data-tour="seller-welcome">
        <h1>Welcome to Your Seller Journey</h1>
        <p>
          This portfolio is the listing brief your agent can actually use — what the home is,
          what you have updated, what still needs attention, and how ready you are to go live.
        </p>
      </section>

      <div className="row start-actions">
        <SharedButton variant="primary" onClick={beginPortfolio}>
          Begin seller portfolio
        </SharedButton>
      </div>

      <SharedCard title="How the process works">
        <ol className="process-list">
          <li>Record the property facts: address, size, type, parking, and occupancy.</li>
          <li>Inventory interior and exterior features as they exist today — not as you wish they were.</li>
          <li>List upgrades, condition notes, photos, and any work you still plan to do.</li>
          <li>Score listing readiness, review seller tips, then share the portfolio with your agent.</li>
        </ol>
        <SharedProgressBar percent={progress.percent} label="Saved portfolio progress" />
      </SharedCard>

      <SharedCard title="Owner snapshot" className="stack" style={{ marginTop: "1rem" }}>
        <div className="two-col">
          <SharedFormField label="Your name" name="ownerName" value={form.ownerName} onChange={update} />
          <SharedFormField label="Email" name="email" type="email" value={form.email} onChange={update} />
        </div>
        <SharedFormField label="Phone" name="phone" type="tel" value={form.phone} onChange={update} />
        <ChoiceGroup
          label="Current occupancy"
          name="occupancy"
          value={form.occupancy}
          onChange={update}
          options={[
            { value: "owner-occupied", label: "Owner occupied" },
            { value: "tenant", label: "Tenant occupied" },
            { value: "vacant", label: "Vacant" },
          ]}
        />
        <ChoiceGroup
          label="Ideal listing timeline"
          name="timeline"
          value={form.timeline}
          onChange={update}
          options={[
            { value: "asap", label: "As soon as ready" },
            { value: "30-60", label: "30–60 days" },
            { value: "this-season", label: "This season" },
            { value: "exploring", label: "Exploring only" },
          ]}
        />
        <SharedFormField
          label="Why you are selling"
          name="reason"
          type="textarea"
          rows={3}
          value={form.reason}
          onChange={update}
          placeholder="Relocation, downsizing, investment, etc. — only what you want your agent to know."
        />
      </SharedCard>
    </AppLayout>
  );
}
